import React, { useState } from 'react';
import PageInstructions from "@/components/PageInstructions";
import { Calculator, TrendingUp, RefreshCw, Key } from 'lucide-react';
import { toast } from 'sonner';

const CODIGO_BASE = 663; // "meia meia três"

const SomaBahiaCalculator = () => {
  const [resultado, setResultado] = useState('');
  const [calculos, setCalculos] = useState<any>(null);
  const [showTutorial, setShowTutorial] = useState(true);

  // Tabela de bichos do jogo do bicho
  const bichos = [
    { num: '01', nome: 'Avestruz', grupos: ['01','02','03','04'] },
    { num: '02', nome: 'Águia', grupos: ['05','06','07','08'] },
    { num: '03', nome: 'Burro', grupos: ['09','10','11','12'] },
    { num: '04', nome: 'Borboleta', grupos: ['13','14','15','16'] },
    { num: '05', nome: 'Cachorro', grupos: ['17','18','19','20'] },
    { num: '06', nome: 'Cabra', grupos: ['21','22','23','24'] },
    { num: '07', nome: 'Carneiro', grupos: ['25','26','27','28'] },
    { num: '08', nome: 'Camelo', grupos: ['29','30','31','32'] },
    { num: '09', nome: 'Cobra', grupos: ['33','34','35','36'] },
    { num: '10', nome: 'Coelho', grupos: ['37','38','39','40'] },
    { num: '11', nome: 'Cavalo', grupos: ['41','42','43','44'] },
    { num: '12', nome: 'Elefante', grupos: ['45','46','47','48'] },
    { num: '13', nome: 'Galo', grupos: ['49','50','51','52'] },
    { num: '14', nome: 'Gato', grupos: ['53','54','55','56'] },
    { num: '15', nome: 'Jacaré', grupos: ['57','58','59','60'] },
    { num: '16', nome: 'Leão', grupos: ['61','62','63','64'] },
    { num: '17', nome: 'Macaco', grupos: ['65','66','67','68'] },
    { num: '18', nome: 'Porco', grupos: ['69','70','71','72'] },
    { num: '19', nome: 'Pavão', grupos: ['73','74','75','76'] },
    { num: '20', nome: 'Peru', grupos: ['77','78','79','80'] },
    { num: '21', nome: 'Touro', grupos: ['81','82','83','84'] },
    { num: '22', nome: 'Tigre', grupos: ['85','86','87','88'] },
    { num: '23', nome: 'Urso', grupos: ['89','90','91','92'] },
    { num: '24', nome: 'Veado', grupos: ['93','94','95','96'] },
    { num: '25', nome: 'Vaca', grupos: ['97','98','99','00'] }
  ];

  const getBichoFromNumber = (num: number) => {
    const lastTwo = String(Math.abs(num) % 100).padStart(2, '0');
    for (let bicho of bichos) {
      if (bicho.grupos.includes(lastTwo)) {
        return bicho;
      }
    }
    return null;
  };

  const formatarMilhar = (num: number) => {
    const absNum = Math.abs(num) % 10000;
    return String(absNum).padStart(4, '0');
  };

  const calcular = () => {
    if (!resultado || resultado.length < 1) {
      toast.error('Por favor, digite o resultado do sorteio!');
      return;
    }

    const numResultado = parseInt(resultado);
    
    // Operações com código base 0663
    const adicao = numResultado + CODIGO_BASE;
    const subtracao = Math.abs(numResultado - CODIGO_BASE);
    const multiplicacao = numResultado * CODIGO_BASE;
    const divisao = Math.floor(numResultado / CODIGO_BASE);

    // Extrair milhares válidos (4 últimos dígitos)
    const resultados = [
      { operacao: 'Adição', simbolo: '+', calculo: `${resultado} + 0663`, valor: adicao, milhar: formatarMilhar(adicao), bicho: getBichoFromNumber(adicao) },
      { operacao: 'Subtração', simbolo: '-', calculo: `${resultado} - 0663`, valor: subtracao, milhar: formatarMilhar(subtracao), bicho: getBichoFromNumber(subtracao) },
      { operacao: 'Multiplicação', simbolo: '×', calculo: `${resultado} × 0663`, valor: multiplicacao, milhar: formatarMilhar(multiplicacao), bicho: getBichoFromNumber(multiplicacao) },
      { operacao: 'Divisão', simbolo: '÷', calculo: `${resultado} ÷ 0663`, valor: divisao, milhar: formatarMilhar(divisao), bicho: getBichoFromNumber(divisao) }
    ];

    setCalculos({
      resultadoOriginal: resultado,
      codigoBase: '0663',
      operacoes: resultados
    });

    toast.success('Cálculo realizado com sucesso!');
  };

  const limpar = () => {
    setResultado('');
    setCalculos(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-900 via-yellow-900 to-orange-800 p-4 rounded-xl">
      <div className="max-w-4xl mx-auto">
        <PageInstructions
          title="📖 Como usar a Soma Chave de Ouro"
          steps={[
            "Digite o resultado do 1º prêmio no campo indicado",
            "O sistema aplica automaticamente o código base (663) na soma",
            "Clique em 'Calcular' para gerar os palpites",
            "Veja o milhar, centena, dezena e grupo resultantes",
            "O bicho correspondente é identificado automaticamente",
            "Use os números gerados para suas apostas do dia"
          ]}
        />
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 mb-6 border border-white/20">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">🔑 Soma Chave de Ouro</h1>
              <p className="text-yellow-200">Sistema Matemático com Código Base</p>
            </div>
            <Key className="w-16 h-16 text-yellow-300" />
          </div>
        </div>

        {/* Chave Matemática Info */}
        <div className="bg-gradient-to-r from-yellow-500/20 to-amber-500/20 backdrop-blur-lg rounded-2xl p-6 mb-6 border-2 border-yellow-400/50">
          <h2 className="text-2xl font-bold text-yellow-300 mb-4 flex items-center gap-2">
            🔐 Chave Matemática
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-white mb-2"><strong>Código Base:</strong></p>
              <div className="bg-yellow-500/30 rounded-xl p-4 text-center">
                <span className="text-4xl font-mono font-bold text-yellow-300">0663</span>
                <p className="text-yellow-200 text-sm mt-2">(chamado de "meia meia três")</p>
              </div>
            </div>
            <div>
              <p className="text-white mb-2"><strong>Operações:</strong></p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-green-500/20 rounded-lg p-3 text-center border border-green-400/30">
                  <span className="text-2xl">➕</span>
                  <p className="text-green-300 text-sm">Adição</p>
                </div>
                <div className="bg-red-500/20 rounded-lg p-3 text-center border border-red-400/30">
                  <span className="text-2xl">➖</span>
                  <p className="text-red-300 text-sm">Subtração</p>
                </div>
                <div className="bg-blue-500/20 rounded-lg p-3 text-center border border-blue-400/30">
                  <span className="text-2xl">✖️</span>
                  <p className="text-blue-300 text-sm">Multiplicação</p>
                </div>
                <div className="bg-purple-500/20 rounded-lg p-3 text-center border border-purple-400/30">
                  <span className="text-2xl">➗</span>
                  <p className="text-purple-300 text-sm">Divisão</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-white/10 rounded-xl">
            <p className="text-yellow-200 text-sm">
              <strong>📋 Processo:</strong> Pegue o resultado de um sorteio e aplique as 4 operações com a chave 0663. 
              Os resultados indicam números que podem sair nos próximos sorteios.
            </p>
          </div>
        </div>

        {/* Tutorial Modal */}
        {showTutorial && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowTutorial(false)}
          >
            <div 
              className="bg-gradient-to-br from-amber-900 to-yellow-900 rounded-2xl p-4 sm:p-6 md:p-8 max-w-2xl w-full border-2 border-yellow-400/50 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-yellow-300 mb-4 sm:mb-6 text-center">
                🔑 Método Soma Chave de Ouro
              </h2>
              
              <div className="space-y-4">
                {/* Explicação da Chave */}
                <div className="bg-yellow-500/20 rounded-xl p-4 border border-yellow-400/50">
                  <h3 className="text-xl font-bold text-yellow-300 mb-3">Chave Matemática</h3>
                  <div className="text-white space-y-2">
                    <p><strong>Código base:</strong> <span className="font-mono text-yellow-300">0663</span> (chamado de "meia meia três")</p>
                    <p><strong>Operações:</strong> Adição (+), Subtração (-), Multiplicação (×) e Divisão (÷)</p>
                  </div>
                </div>

                {/* Processo */}
                <div className="bg-white/10 rounded-xl p-4 border border-white/20">
                  <h3 className="text-xl font-bold text-white mb-3">📋 Processo</h3>
                  <ol className="text-yellow-200 space-y-2 list-decimal list-inside">
                    <li>Pegue o resultado de um sorteio (ex: 0266 da São Paulo 8h)</li>
                    <li>Aplique as operações com a chave 0663</li>
                    <li>Os resultados indicam números para os próximos sorteios</li>
                  </ol>
                </div>

                {/* Exemplo */}
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl p-4 border border-green-400/50">
                  <h3 className="text-xl font-bold text-green-300 mb-3">📊 Exemplo com 0266</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-white text-sm">
                    <div className="bg-white/10 rounded-lg p-2">
                      <span className="text-green-300">Adição:</span> 0266 + 0663 = <strong>0929</strong>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <span className="text-red-300">Subtração:</span> 0266 - 0663 = <strong>0397</strong>
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <span className="text-blue-300">Multiplicação:</span> últimos 4 dígitos
                    </div>
                    <div className="bg-white/10 rounded-lg p-2">
                      <span className="text-purple-300">Divisão:</span> resultado inteiro
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowTutorial(false)}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white font-bold py-3 px-6 rounded-xl transition shadow-lg"
                >
                  ✅ Entendi! Vamos Começar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Botão Tutorial */}
        <div className="mb-4 flex justify-end">
          <button
            onClick={() => setShowTutorial(true)}
            className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-amber-900 font-semibold px-4 py-2 rounded-lg transition shadow-lg"
          >
            <span className="text-xl">📚</span>
            Ver Tutorial
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Painel de Entrada */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-yellow-300" />
              Resultado do Sorteio
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-white mb-2">
                  Digite o resultado (milhar do sorteio):
                </label>
                <input
                  type="text"
                  value={resultado}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setResultado(val);
                  }}
                  placeholder="Ex: 0266"
                  className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white text-2xl font-mono text-center focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  maxLength={4}
                />
                <p className="text-yellow-200 text-sm mt-2">
                  💡 Digite o resultado que saiu no sorteio (ex: São Paulo 8h, Federal, etc.)
                </p>
              </div>

              {/* Mostrar código base */}
              <div className="bg-yellow-500/20 rounded-lg p-4 border border-yellow-400/30">
                <p className="text-yellow-200 text-sm mb-1">Chave Base Utilizada:</p>
                <p className="text-yellow-300 text-3xl font-mono font-bold text-center">0663</p>
                <p className="text-yellow-200 text-xs text-center mt-1">"meia meia três"</p>
              </div>

              {/* Botões */}
              <div className="flex gap-3">
                <button
                  onClick={calcular}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white font-bold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2"
                >
                  <Calculator className="w-5 h-5" />
                  Calcular
                </button>
                <button
                  onClick={limpar}
                  className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-lg transition flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  Limpar
                </button>
              </div>
            </div>
          </div>

          {/* Painel de Resultados */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Key className="w-6 h-6 text-yellow-300" />
              Resultados das Operações
            </h2>

            {calculos ? (
              <div className="space-y-4">
                {/* Info do cálculo */}
                <div className="bg-yellow-500/20 border border-yellow-400/50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-yellow-200 text-sm">Resultado Utilizado:</p>
                      <p className="text-yellow-300 text-2xl font-mono font-bold">{calculos.resultadoOriginal.padStart(4, '0')}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-yellow-200 text-sm">Chave Base:</p>
                      <p className="text-yellow-300 text-2xl font-mono font-bold">0663</p>
                    </div>
                  </div>
                </div>

                {/* Operações */}
                <div className="space-y-3">
                  {calculos.operacoes.map((op: any, idx: number) => (
                    <div 
                      key={idx} 
                      className={`rounded-lg p-4 border ${
                        idx === 0 ? 'bg-green-500/20 border-green-400/50' :
                        idx === 1 ? 'bg-red-500/20 border-red-400/50' :
                        idx === 2 ? 'bg-blue-500/20 border-blue-400/50' :
                        'bg-purple-500/20 border-purple-400/50'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className={`text-sm font-semibold ${
                            idx === 0 ? 'text-green-300' :
                            idx === 1 ? 'text-red-300' :
                            idx === 2 ? 'text-blue-300' :
                            'text-purple-300'
                          }`}>
                            {op.operacao} ({op.simbolo})
                          </p>
                          <p className="text-white/80 text-xs font-mono">{op.calculo} = {op.valor}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-white text-2xl font-mono font-bold">{op.milhar}</p>
                          {op.bicho && (
                            <p className="text-yellow-300 text-sm">{op.bicho.nome} ({op.bicho.num})</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Resumo dos Bichos */}
                <div className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 border border-purple-400/50 rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-2">🐾 Bichos Sugeridos:</h3>
                  <div className="flex flex-wrap gap-2">
                    {calculos.operacoes
                      .filter((op: any) => op.bicho)
                      .map((op: any, idx: number) => (
                        <div key={idx} className="bg-yellow-400 text-purple-900 font-bold px-3 py-1 rounded-lg text-sm">
                          {op.bicho.num} - {op.bicho.nome}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Key className="w-16 h-16 text-white/30 mx-auto mb-4" />
                <p className="text-white/60">Digite o resultado do sorteio e clique em Calcular</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center text-white/60 text-sm">
          <p>🔑 Soma Chave de Ouro - Código Base 0663</p>
          <p className="mt-1">⚠️ Use com responsabilidade - Resultados não garantidos</p>
        </div>
      </div>
    </div>
  );
};

export default SomaBahiaCalculator;
